import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, CalendarDays, Clock, Flag, CheckCircle2, Circle } from 'lucide-react';
import { colorForEvent, CATEGORY_BY_ID } from '../lib/categories';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const PRIORITY_COLORS = {
  low: '#A3D4D4',
  medium: '#EAB308',
  urgent: '#EF4444',
};

function fmtDate(d) { return d.toISOString().slice(0, 10); }

function buildMonthGrid(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const startDow = first.getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(Date.UTC(year, month, d)));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function PublicCalendarSection() {
  const [events, setEvents] = useState([]);
  const [roadmaps, setRoadmaps] = useState([]);
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getUTCFullYear(), m: today.getUTCMonth() });
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/planning/events-public`),
      axios.get(`${API}/planning/roadmaps`),
      axios.get(`${API}/pacer/calendar`).catch(() => ({ data: { events: [] } })),
    ])
      .then(([evRes, rmRes, paRes]) => {
        const baseEvents = evRes.data || [];
        const pacerEvents = (paRes.data?.events || []).map((e) => ({
          ...e,
          // Mark with kind so colorForEvent can branch; also push priority-style colour
          kind: 'pacer',
        }));
        setEvents([...baseEvents, ...pacerEvents]);
        setRoadmaps(rmRes.data);
      })
      .catch(() => {});
  }, []);

  const phaseById = useMemo(() => {
    const m = {};
    roadmaps.forEach((rm) => (rm.phases || []).forEach((p) => { m[p.id] = { ...p, roadmap: rm }; }));
    return m;
  }, [roadmaps]);

  const cells = useMemo(() => buildMonthGrid(cursor.y, cursor.m), [cursor]);
  const eventsByDate = useMemo(() => {
    const map = {};
    const addDays = (iso, n) => {
      const d = new Date(iso + 'T00:00:00Z');
      d.setUTCDate(d.getUTCDate() + n);
      return d.toISOString().slice(0, 10);
    };
    events.forEach((e) => {
      const start = e.start_date && e.start_date <= e.due_date ? e.start_date : e.due_date;
      const end = e.due_date;
      let day = start;
      let safety = 0;
      while (day <= end && safety < 365) {
        const span = (start === end)
          ? 'single'
          : day === start ? 'start' : day === end ? 'end' : 'middle';
        (map[day] = map[day] || []).push({ ...e, _span: span });
        day = addDays(day, 1);
        safety += 1;
      }
    });
    return map;
  }, [events]);

  const monthEvents = useMemo(() => {
    const prefix = `${cursor.y}-${String(cursor.m + 1).padStart(2, '0')}`;
    return events
      .filter((e) => e.due_date?.startsWith(prefix))
      .sort((a, b) => a.due_date.localeCompare(b.due_date));
  }, [events, cursor]);

  const prevMonth = () => setCursor((c) => c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 });
  const nextMonth = () => setCursor((c) => c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 });
  const goToday = () => setCursor({ y: today.getUTCFullYear(), m: today.getUTCMonth() });

  if (events.length === 0 && roadmaps.length === 0) return null;

  return (
    <section data-testid="public-calendar-section" className="py-14 sm:py-20 px-4 md:px-8" style={{ backgroundColor: 'white' }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.2em] font-bold mb-2" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>
            <CalendarDays className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Reconstruction Calendar
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
            Key Dates & Milestones
          </h2>
          <p className="mt-3 text-sm" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
            Follow along as we rebuild — click any event to see details.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2 p-4 sm:p-5 rounded-2xl" style={{ backgroundColor: '#F4F1EB' }}>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <button data-testid="pub-cal-prev" onClick={prevMonth} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#EAE5D9' }}>
                  <ChevronLeft className="w-4 h-4" style={{ color: '#1A2E2E' }} />
                </button>
                <h3 className="text-xl font-medium px-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
                  {MONTHS[cursor.m]} {cursor.y}
                </h3>
                <button data-testid="pub-cal-next" onClick={nextMonth} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#EAE5D9' }}>
                  <ChevronRight className="w-4 h-4" style={{ color: '#1A2E2E' }} />
                </button>
              </div>
              <button onClick={goToday} className="text-[10px] font-bold px-3 py-1.5 rounded-lg" style={{ backgroundColor: '#1A2E2E', color: 'white', fontFamily: 'Manrope' }}>
                Today
              </button>
            </div>

            <div className="grid grid-cols-7 gap-px mb-1">
              {WEEKDAYS.map((w) => (
                <div key={w} className="text-[10px] font-bold uppercase tracking-wider text-center py-1" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                  {w}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {cells.map((date, i) => {
                if (!date) return <div key={`e${i}`} className="aspect-square" />;
                const key = fmtDate(date);
                const dayEvents = eventsByDate[key] || [];
                const isToday = key === fmtDate(today);
                return (
                  <div
                    key={key}
                    data-testid={`pub-cal-cell-${key}`}
                    className="aspect-square p-1 sm:p-1.5 rounded-lg border relative flex flex-col overflow-hidden"
                    style={{
                      borderColor: isToday ? '#1F4FBE' : 'rgba(0,0,0,0.06)',
                      backgroundColor: isToday ? '#1F4FBE' : 'white',
                      color: isToday ? 'white' : '#1A2E2E',
                    }}
                  >
                    <span className="text-[10px] sm:text-xs font-bold" style={{ fontFamily: 'Manrope' }}>{date.getUTCDate()}</span>
                    <div className="flex-1 mt-0.5 space-y-0.5">
                      {dayEvents.slice(0, 2).map((e) => {
                        const color = colorForEvent(e, phaseById, PRIORITY_COLORS);
                        const isMulti = e._span && e._span !== 'single';
                        const progress = Math.max(0, Math.min(100, Number(e.progress_percent) || 0));
                        // Multi-day chips merge visually: start gets rounded-l, end rounded-r,
                        // middle gets no rounding so they look like one continuous bar.
                        const radii = e._span === 'start'
                          ? '4px 0 0 4px'
                          : e._span === 'end'
                            ? '0 4px 4px 0'
                            : e._span === 'middle'
                              ? '0'
                              : '4px';
                        return (
                          <button
                            key={`${e.id}-${e._span || 'single'}`}
                            data-testid={`pub-cal-event-${e.id}`}
                            onClick={() => setSelectedEvent(e)}
                            className="block w-full text-[8px] sm:text-[9px] font-bold text-left cursor-pointer relative overflow-hidden"
                            style={{
                              // Stronger phase-color tint (40% opacity) so multi-day bars are clearly
                              // differentiable at a glance — orange for Strategic Planning, blue for
                              // Brand & Experience Prep, etc. Completed events override with green.
                              backgroundColor: e.completed
                                ? '#22C55E33'
                                : isToday ? 'rgba(255,255,255,0.18)' : `${color}66`,
                              color: isToday ? 'white' : '#1A2E2E',
                              textDecoration: e.completed ? 'line-through' : 'none',
                              borderRadius: radii,
                              borderLeft: (e._span === 'start' || e._span === 'single' || !e._span)
                                ? `3px solid ${color}`
                                : 'none',
                              // Solid 2px top stripe in phase color on EVERY span of a multi-day bar
                              // so the colored line stays unbroken across day cells (start → middle → end).
                              borderTop: isMulti ? `2px solid ${color}` : 'none',
                              marginRight: isMulti && e._span !== 'end' && e._span !== 'single' ? '-4px' : 0,
                              marginLeft: isMulti && e._span !== 'start' && e._span !== 'single' ? '-4px' : 0,
                            }}
                            title={
                              (isMulti ? `${e.title} · ${e.start_date || e.due_date} → ${e.due_date}` : e.title)
                              + ` · ${progress}% complete`
                            }
                          >
                            <span
                              aria-hidden
                              className="absolute inset-y-0 left-0"
                              style={{
                                width: `${progress}%`,
                                backgroundColor: e.completed ? '#22C55E' : color,
                                opacity: e.completed ? 0.55 : 0.85,
                                transition: 'width 200ms ease-out',
                              }}
                            />
                            <span className="relative z-10 block px-1 py-0.5 truncate">
                              {e._span === 'start' || e._span === 'single' || !e._span
                                ? `${e.completed ? '✓ ' : ''}${e.title}${progress > 0 && progress < 100 ? ` · ${progress}%` : ''}`
                                : '\u00A0'}
                            </span>
                          </button>
                        );
                      })}
                      {dayEvents.length > 2 && (
                        <span className="text-[8px] italic" style={{ opacity: 0.7 }}>+{dayEvents.length - 2}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Month events list */}
          <aside className="p-4 sm:p-5 rounded-2xl" style={{ backgroundColor: '#F4F1EB' }}>
            <h3 className="text-sm font-bold mb-3 flex items-center gap-1.5" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>
              <CalendarDays className="w-4 h-4" /> {MONTHS[cursor.m]} events ({monthEvents.length})
            </h3>
            {monthEvents.length === 0 && (
              <p className="text-xs italic" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                No events scheduled this month.
              </p>
            )}
            <div className="space-y-2 max-h-[26rem] overflow-y-auto pr-1">
              {monthEvents.map((e) => {
                const color = colorForEvent(e, phaseById, PRIORITY_COLORS);
                const phase = phaseById[e.phase_id];
                const cat = e.category && CATEGORY_BY_ID[e.category];
                const progress = Math.max(0, Math.min(100, Number(e.progress_percent) || 0));
                return (
                  <button
                    key={e.id}
                    onClick={() => setSelectedEvent(e)}
                    data-testid={`pub-event-${e.id}`}
                    className="w-full text-left p-3 rounded-xl transition-all hover:-translate-y-0.5 flex items-start gap-2"
                    style={{ backgroundColor: 'white', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}
                  >
                    <div className="mt-0.5 flex-shrink-0">
                      {e.completed
                        ? <CheckCircle2 className="w-4 h-4" style={{ color: '#22C55E' }} />
                        : <Circle className="w-4 h-4" style={{ color }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate" style={{ fontFamily: 'Manrope', color: '#1A2E2E', textDecoration: e.completed ? 'line-through' : 'none' }}>{e.title}</p>
                      <div className="flex flex-wrap gap-2 mt-0.5 text-[10px]" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>
                        <span>{e.due_date}{e.due_time ? ` · ${e.due_time}` : ''}</span>
                        {cat && <span>· {cat.label}</span>}
                        {!cat && phase && <span>· {phase.name.split(':')[0] || phase.name}</span>}
                      </div>
                      {/* Progress strip — same number that's shown on the calendar bar */}
                      <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#EAE5D9' }}>
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${progress}%`, backgroundColor: e.completed ? '#22C55E' : color }}
                        />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>
        </div>
      </div>

      {/* Event detail lightbox */}
      {selectedEvent && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/80 p-4"
          style={{ zIndex: 10002 }}
          onClick={() => setSelectedEvent(null)}
          data-testid="pub-event-detail"
        >
          <div
            className="w-full max-w-md rounded-2xl p-5 sm:p-6 relative"
            style={{ backgroundColor: '#F4F1EB' }}
            onClick={(ev) => ev.stopPropagation()}
          >
            <button
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#EAE5D9', color: '#4A5D5D' }}
              onClick={() => setSelectedEvent(null)}
            >✕</button>

            <div className="flex flex-wrap items-center gap-2 mb-2">
              {selectedEvent.completed
                ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: '#22C55E', fontFamily: 'Manrope' }}>✓ COMPLETED</span>
                : (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white flex items-center gap-1" style={{ backgroundColor: PRIORITY_COLORS[selectedEvent.priority], fontFamily: 'Manrope' }}>
                    <Flag className="w-2.5 h-2.5" /> {selectedEvent.priority.toUpperCase()}
                  </span>
                )}
              {selectedEvent.category && CATEGORY_BY_ID[selectedEvent.category] && (
                <span
                  className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white flex items-center gap-1.5"
                  style={{ backgroundColor: CATEGORY_BY_ID[selectedEvent.category].color, fontFamily: 'Manrope' }}
                  data-testid="pub-event-category"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  {CATEGORY_BY_ID[selectedEvent.category].label}
                </span>
              )}
              {!selectedEvent.category && phaseById[selectedEvent.phase_id] && (
                <span className="text-[10px] font-bold" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                  {phaseById[selectedEvent.phase_id].roadmap.name} · {phaseById[selectedEvent.phase_id].name}
                </span>
              )}
            </div>

            <h3 className="text-2xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>{selectedEvent.title}</h3>
            <p className="text-xs flex items-center gap-1 mb-3" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
              <Clock className="w-3 h-3" /> {selectedEvent.due_date}{selectedEvent.due_time ? ` · ${selectedEvent.due_time}` : ''}
            </p>

            {/* Progress — same percent stored on the event, mirrors what the
                admin Event editor sets and what the calendar bar reflects. */}
            {(() => {
              const progress = Math.max(0, Math.min(100, Number(selectedEvent.progress_percent) || 0));
              const accent = colorForEvent(selectedEvent, phaseById, PRIORITY_COLORS);
              return (
                <div className="p-3 rounded-xl mb-3" style={{ backgroundColor: 'white' }} data-testid="pub-event-progress">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[11px] font-bold uppercase tracking-wider" style={{ fontFamily: 'Manrope', color: '#4A5D5D' }}>
                      Progress
                    </p>
                    <p className="text-sm font-bold tabular-nums" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>
                      {progress}%
                    </p>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#EAE5D9' }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${progress}%`, backgroundColor: selectedEvent.completed ? '#22C55E' : accent }}
                    />
                  </div>
                  {selectedEvent.status && (
                    <p className="text-[10px] mt-1.5" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>
                      Status: <span style={{ color: '#1A2E2E', fontWeight: 700 }}>{selectedEvent.status.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                    </p>
                  )}
                </div>
              );
            })()}

            {selectedEvent.description && (
              <p className="text-sm leading-relaxed mb-3" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }} data-testid="pub-event-description">
                {selectedEvent.description}
              </p>
            )}
            {selectedEvent.checklist_total > 0 && (
              <div className="p-3 rounded-xl" style={{ backgroundColor: 'white' }}>
                <p className="text-[11px] font-bold mb-1" style={{ fontFamily: 'Manrope', color: '#1F4FBE' }}>
                  Sub-tasks: {selectedEvent.checklist_done} / {selectedEvent.checklist_total} done
                </p>
                <div className="h-1.5 rounded-full" style={{ backgroundColor: '#EAE5D9' }}>
                  <div className="h-full rounded-full" style={{
                    width: `${selectedEvent.checklist_total ? (selectedEvent.checklist_done / selectedEvent.checklist_total) * 100 : 0}%`,
                    backgroundColor: '#1F4FBE',
                  }} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
